import React, { useState } from 'react';
import { Container, Button, useMantineColorScheme, Text, Group } from '@mantine/core';
import ModalComponent from '../modal';
import { fetchReceiptData } from '../../../libraries/apm';
import { useSelector } from 'react-redux';
import { AppState } from '../../client-redux/reducers';
import { Language } from '../../languages/languageHandler';
import * as Icon from '@tabler/icons-react';
import './styles.scss';

interface ShowReceiptProps {
  a001: number;
  movType: string;
  a031: number;
  a032: number;
  a029: number;
  dateTime: string;
}

interface PrintComponentProps {
  receiptInfo: string;
}

const PrintComponent: React.FC<PrintComponentProps> = ({ receiptInfo }) => {
  const printDivContent = () => {
    const newWin = window.open('', 'Print-Window');

    if (newWin) {
      newWin.document.open();
      newWin.document.write(
        '<html><body onload="window.print()" style="font-family:courier;font-size: 12px;">' +
          receiptInfo +
          '</body></html>'
      );
      newWin.document.close();
      newWin.close();
    } else {
      console.error('Failed to open print window');
    }
  };

  return <Icon.IconPrinter stroke={2} onClick={printDivContent} />;
};

const APMReceipt: React.FC<ShowReceiptProps> = ({ a001, movType, a031, a032, a029, dateTime }) => {
  const controller = new AbortController();
  const lang = useSelector<AppState, Language>(state => state.lang.lang);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [receiptInfo, setReceiptInfo] = useState<string>('');
  const { colorScheme } = useMantineColorScheme();
  const [noResults, setNoResults] = useState<boolean>(true);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const openModal = async () => {
    if (showModal) return;

    setShowModal(true);
    setNoResults(true);
    setErrorMessage('');
    setReceiptInfo('');

    try {
      const response = await fetchReceiptData(a001, `${movType}`, a031, a032, a029, dateTime, controller);

      if (response?.statusOper?.code === 0) {
        if (response?.receipt) {
          const decodedReceipt = decodeURIComponent(escape(atob(response.receipt)));
          const formattedReceipt = decodedReceipt.replace(/ /g, '&nbsp;').replace(/\n/g, '<br />');
          setReceiptInfo(formattedReceipt);
          setNoResults(false);
        } else {
          setNoResults(true);
          setErrorMessage(lang.PERIODS_RECEIPT_NO_DATA);
        }
      } else if (response?.statusOper?.code === 1) {
        setNoResults(true);
        setErrorMessage(lang.PERIODS_RECEIPT_NO_DATA);
      } else {
        setNoResults(true);
        setErrorMessage(response?.statusOper?.message || lang.PERIODS_RECEIPT_ERROR);
      }
    } catch (error) {
      console.error('Error fetching receipt data:', error);
      setNoResults(true);
      setErrorMessage(lang.PERIODS_RECEIPT_ERROR);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    controller.abort();
  };

  return (
    <>
      <Icon.IconReceipt stroke={2} onClick={openModal} />
      <ModalComponent
        show={showModal}
        handleClick={closeModal}
        title={lang.GLOBAL_RECEIPT}
        content={
          <>
            <Container bg={colorScheme === 'dark' ? '#1b1b1b' : 'white'} classNames={{ root: 'APMReceipt' }}>
              <Group className="apmReceiptDetails">
                {noResults ? (
                  <Text style={{ textAlign: 'center' }}>{errorMessage || lang.GLOBAL_NO_RESULTS}</Text>
                ) : (
                  <Text dangerouslySetInnerHTML={{ __html: receiptInfo }} />
                )}
              </Group>

              <Group>
                <Button type="button" color="#016273" radius="md" disabled={noResults}>
                  <PrintComponent receiptInfo={receiptInfo} />
                </Button>
              </Group>
            </Container>
          </>
        }
      />
    </>
  );
};

export default APMReceipt;
